import {
  DocumentSigningStatus,
  GetDocumentSigningStatusResponse,
  GetSubscriptionStatusResponse,
} from "@/document/documentApi";
import { CompanyItem, failure, mapToSigners, ok, Portfolio, SigningMethod } from "@/common/common";
import { ArchiveDocumentType } from "@/archive/commonArchiveDomain";
import {
  closeSignDocumentDialog,
  dissolvePortfolio,
  filterCompaniesBySignature,
  GetPortfolioCompanySignersResponse,
  InvalidPortfolioIdState,
  isSamePortfolio,
  LoadingPortfolioSignaturesState,
  mapFilesToPreviewFromSubscriptionStatus,
  navigateToSignaturesTabAction,
  navigateToSignaturesTabInContextCompany,
  openSelectDocumentsForSigningDialog,
  openSendDocumentsForSigningDialog,
  portfolioCreationInfo,
  PortfolioHeader,
  PortfolioPageDispatcher,
  PortfolioPageStore,
  PortfolioSettingsActions,
  PortfolioSignaturesReadyState,
  PortfolioSignaturesResponse,
  PortfolioSignaturesState,
  PortfolioSignaturesTab,
  savePortfolioSettings,
  SelectedPortfolioOption,
  toFollowUpCounts,
  toggleConfirmDissolvePortfolio,
  togglePortfolioSettings,
  toPortfolioApiUrl,
  validateDocumentsForSigning,
} from "@/portfolio/portfolioDomain";
import { DocumentType } from "@/common/documents";
import { Language } from "@/i18n/languages";
import { formatDateTime } from "@/i18n/dateFormat";
import {
  DCEvent,
  EventDispatcher,
  httpGet,
  httpPost,
  HttpResponse,
  navigate,
  showAlert,
  showGeneralError,
  showVerifyEmailDialog,
} from "@/events/eventsDomain";
import {
  adminArchive,
  adminProjects,
  documentLocation,
  fromSigningMethodToParameter,
} from "@/utils/navigation";
import {
  ResendDocumentForSigningData,
  ViewDocumentSigningStatusDialogModel,
} from "@/dbot/summary/summaryDomain";
import { TMessage } from "@/i18n/tt";
import { IconProps } from "@/components-v2/Icon";
import {
  ArchiveEntry,
  ArchiveRootFolder,
  DestinationFolder,
  DestinationFoldersResponse,
  getSentForSigningTitle,
} from "@/archive/archiveDomain";
import { TabButtonProps } from "@/components-v2/Tabs";
import { compact, noop } from "lodash";
import {
  BatchPreviewModel,
  FileToPreview,
} from "@/components-v2/BatchPreviewDialog/batchPreviewDomain";
import { FilePreviewProps } from "@/components-v2/FilePreview";
import { resolveUrnReference } from "@/utils/UrnReference";
import { FileUploadConfig, toFormData } from "@/fileUpload/fileUploadDomain";
import {
  CompaniesForPreview,
  DocumentsForPreview,
  SignersForm,
  StringFieldModel,
} from "@/common/commonForms";
import { User } from "@/types/UserInfo";
import { ExtendedShareholderDTO } from "@/types/Shareholder";
import { AIClient } from "@/ai/AIAssistant";
import { when } from "@/utils/functions";
import { RootStoreData } from "@/events/rootStoreData";
import { UserPrefs } from "@/stores/userPrefsDomain";
import { AlertProps } from "@/components-v2/Alert";
import { getFullName } from "@/utils/Roles";

export type SignatureItem = {
  title: string;
  company: CompanyItem;
  requestedBy: string;
  sentAt?: string;
  signedAt?: string;
  numberOfPartiesSigned: number;
  totalNumberOfPartiesSentTo: number;
  signingRequestSource: SigningRequestSource;
  signingStatus: DocumentSigningStatus;
  signingMethod: SigningMethod;
  documentId: string;
  documentType: ArchiveDocumentType;
  documentReference: string;
  reference?: string;
  hasAdmin: boolean;
};

export type SignaturesContext = {
  pending: SignatureItem[];
  signed: SignatureItem[];
  sent: SignatureItem[];
  sealed: SignatureItem[];
};

export type SigningRequestSource = SourceArchive | SourceDbot;

export type SourceArchive = {
  type: "archive";
  parentId: string;
  parentTitle: string;
  folderType?: string;
  documentType?: DocumentType;
  archiveRootFolder?: ArchiveRootFolder;
};

export type SourceDbot = {
  type: "dbot";
  projectId: string;
  title: string;
};

export type SendForSigningState = {
  step?: "select-documents" | "send-documents";
  fileUploadConfig: FileUploadConfig;
  sendDocumentForSigningItems: SendDocumentForSigningItem[];
  selectedItemIndex?: number;
  selectedCompanyItemIndex?: number;
  companiesInPortfolio: CompanyItem[];
  shareholders?: Array<ExtendedShareholderDTO>;
  signersForm?: SignersForm;
  isSubmitting?: boolean;
  message?: AlertProps;
};

export const getSigningItemsForSigning = (
  state: SendForSigningState,
): SendDocumentForSigningItem[] => {
  return state.sendDocumentForSigningItems.filter(
    (item) =>
      item.companyItem?.organisationNumber === getSelectedCompanyItem(state)?.organisationNumber,
  );
};

export const getSelectedSigningItem = (
  state: SendForSigningState,
): SendDocumentForSigningItem | undefined => {
  const signingItems = getSigningItemsForSigning(state);
  return signingItems[state.selectedItemIndex ?? 0] ?? signingItems[0];
};

export const getCompanyItemsForSigning = (state: SendForSigningState): CompanyItem[] => {
  return [...new Set(state.sendDocumentForSigningItems.map((item) => item.companyItem))].filter(
    (d) => d !== undefined,
  );
};

export const getSelectedCompanyItem = (state: SendForSigningState): CompanyItem | undefined => {
  const companyItemsForSigning = getCompanyItemsForSigning(state);
  return companyItemsForSigning[state.selectedCompanyItemIndex ?? 0] ?? companyItemsForSigning[0];
};

export type SendDocumentForSigningItem = {
  file: File;
  companyItem?: CompanyItem;
  destinationFolderCandidates: DestinationFolder[];
  destination?: DestinationFolder;
};

export type FileDestination = {
  orgNo: string;
  folderId: string;
  fileName: string;
};

export type SignatureActions = {
  navigateToArchiveFolder: (orgNo: string, folderId: string) => void;
  navigateToProject: (orgNo: string, projectId: string) => void;
  mapToSentForSigningLinkAction: (signature: SignatureItem) => SentForSigningLinkAction;
  getSigningStatus: (signature: SignatureItem) => void;
  openDocument: (signature: SignatureItem, context: PortfolioSignaturesTab) => void;
  closeDocumentPreview: () => void;
  filterSignatures: (locale: Language) => SignatureItem[];
  openBatchSigningPage: (signingMethod: SigningMethod) => void;
  openSendForSigningDialog: () => void;
  selectDocumentsForSigning: (file: File[]) => void;
  removeDocumentForSigning: (file: File) => void;
  openSendDocumentsForSigningDialog: () => void;
  setCompanyItemDestination: (file: File, item: CompanyItem) => void;
  onSelectDestinationFolder: (
    sendForSigningItem: SendDocumentForSigningItem,
    folder: ArchiveEntry,
  ) => void;
  closeForSigningDialog: () => void;
  submitDocumentsForSigning: () => void;
  setSelectedCompanyItemIndex: (index: number) => void;
  nextItem: () => void;
  previousItem: () => void;
  setSelectedItemIndex: (index: number) => void;
  nextCompanyItem: () => void;
  previousCompanyItem: () => void;
  closeDuplicateAlertInfo: () => void;
  verifyEmail: () => void;
} & PortfolioSettingsActions;

export type SentForSigningLinkAction = {
  label?: TMessage;
  title: TMessage;
  onClick: () => void;
  icon: IconProps["name"];
};

export type PreparedPortfolioSignaturesState =
  | { state: InvalidPortfolioIdState }
  | { state: LoadingPortfolioSignaturesState }
  | { state: PortfolioSignaturesReadyState; actions: SignatureActions };

export const signaturesApi = {
  getSignatures: ({
    portfolio,
    contextCompany,
    onSuccess,
    onFailure,
  }: {
    portfolio: SelectedPortfolioOption;
    contextCompany?: string;
    onSuccess: (res: HttpResponse<PortfolioSignaturesResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: contextCompany
        ? `${toPortfolioApiUrl(portfolio)}/signatures/${contextCompany}`
        : `${toPortfolioApiUrl(portfolio)}/signatures`,
      onSuccess,
      onFailure,
    }),

  getSigningStatus: ({
    signatureItem,
    onSuccess,
    onFailure,
  }: {
    signatureItem: SignatureItem;
    onSuccess: (res: HttpResponse<GetDocumentSigningStatusResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: `/document/check-signing-status/${signatureItem.documentId}/${signatureItem.company.organisationNumber}`,
      onSuccess,
      onFailure,
    }),

  resendDocumentForSigning: ({
    signedDocumentId,
    data,
    onSuccess,
    onFailure,
  }: {
    signedDocumentId: string;
    data: ResendDocumentForSigningData;
    onSuccess: (res: HttpResponse<GetDocumentSigningStatusResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `/document/resend-for-signing/${signedDocumentId}`,
      data,
      onSuccess,
      onFailure,
    }),

  getSubscriptionStatus: ({
    signatureItem,
    onSuccess,
    onFailure,
  }: {
    signatureItem: SignatureItem;
    onSuccess: (res: HttpResponse<GetSubscriptionStatusResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: `/document/check-subscription-status/${signatureItem.documentId}/${signatureItem.company.organisationNumber}`,
      onSuccess,
      onFailure,
    }),

  getDestinationFolders: ({
    orgNo,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    onSuccess: (res: HttpResponse<DestinationFoldersResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: `portfolio/signatures/destination-folders/${orgNo}`,
      data: { orgNo },
      onSuccess,
      onFailure,
    }),

  getCompanySigners: ({
    companies,
    onSuccess,
    onFailure,
  }: {
    companies: string[];
    onSuccess: (
      res: HttpResponse<GetPortfolioCompanySignersResponse>,
      store: RootStoreData,
    ) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: "/portfolio/signatures/company-signers",
      data: { companies },
      onSuccess,
      onFailure,
    }),

  submitDocumentsForSigning: ({
    formData,
    onSuccess,
    onFailure,
  }: {
    formData: FormData;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }) =>
    httpPost({
      url: "portfolio/signatures/send-uploaded-document-for-signing",
      data: formData,
      onSuccess,
      onFailure,
    }),

  cancelSignatureRequest: ({
    documentId,
    onSuccess,
    onFailure,
  }: {
    documentId: string;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }) =>
    httpPost({
      url: "sign/cancel-signature-request",
      data: { id: documentId },
      onSuccess,
      onFailure,
    }),

  reopenLockedDocument: ({
    documentId,
    orgNo,
    onSuccess,
    onFailure,
  }: {
    documentId: string;
    orgNo: string;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }) =>
    httpPost({
      url: `/document/reopen-locked-document/${documentId}/${orgNo}`,
      onSuccess,
      onFailure,
    }),
};

export const filterSignaturesWithText = (
  state: PortfolioSignaturesReadyState,
  locale: Language,
): SignatureItem[] => {
  if (state.signaturesResponse.signatures === undefined) {
    return [];
  }
  const input = state.signatureSearchModel.inputValue.toLowerCase();
  const title = (item: SignatureItem) => item.title.toLowerCase().includes(input);
  const companyName = (item: SignatureItem) =>
    item.company.companyName.toLowerCase().includes(input);
  const signingMethod = (item: SignatureItem) => item.signingMethod.toLowerCase().includes(input);
  const sentAt = (item: SignatureItem) =>
    !item.sentAt || formatDateTime(locale, item.sentAt).toLowerCase().includes(input);

  const requestedBy = (item: SignatureItem) =>
    item.requestedBy !== "self" && item.requestedBy.toLowerCase().includes(input);

  const signedAt = (item: SignatureItem) =>
    !item.signedAt || formatDateTime(locale, item.signedAt).toLowerCase().includes(input);

  const filterByCompany = (item: SignatureItem) =>
    state.companyFilter.value === null ||
    item.company.companyName === state.companyFilter.value?.companyName;

  let signaturesTab = state.signaturesTab;
  if (!signaturesTab) {
    //TODO: temp fix for missing tab, should be removed, once we have an implementation for the automatic selection of the active tab
    signaturesTab = state.signaturesTabs.find((s) => s.isActive)?.id as PortfolioSignaturesTab;
  }

  switch (signaturesTab) {
    case "pending":
      return state.signaturesResponse.signatures.pending.filter(
        (i) =>
          (title(i) || companyName(i) || signingMethod(i) || sentAt(i) || requestedBy(i)) &&
          filterByCompany(i),
      );
    case "signed":
      return state.signaturesResponse.signatures.signed.filter(
        (i) =>
          (title(i) || companyName(i) || signedAt(i) || sentAt(i) || requestedBy(i)) &&
          filterByCompany(i),
      );
    case "sent":
      return state.signaturesResponse.signatures.sent.filter(
        (i) => (title(i) || companyName(i) || sentAt(i) || requestedBy(i)) && filterByCompany(i),
      );
    case "sealed":
      return state.signaturesResponse.signatures.sealed.filter(
        (i) => (title(i) || companyName(i) || signedAt(i)) && filterByCompany(i),
      );
    default:
      return [];
  }
};

export const closeDocumentPreview = (state: PortfolioSignaturesReadyState): DCEvent[] => {
  return [updateSignaturesStore(state, { documentPreview: undefined })];
};

export const openBatchSigningPage = (signingMethod: SigningMethod): DCEvent[] => [
  navigate(`/signing/${fromSigningMethodToParameter(signingMethod)}`),
];

export const openDocument = (
  state: PortfolioSignaturesReadyState,
  context: PortfolioSignaturesTab,
  signature: SignatureItem,
): DCEvent[] => {
  if (context === "pending") {
    return [
      navigate(
        `/signing/${fromSigningMethodToParameter(signature.signingMethod)}?ref=${signature.documentReference}&mode=single`,
      ),
    ];
  }
  const isSealed = signature.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE;
  const files = (): FileToPreview[] => {
    switch (context) {
      case "sent":
      case "signed":
        return [{ reference: isSealed ? signature.documentId : signature.documentReference }];
      case "sealed":
        return [{ reference: signature.documentId }];
      default:
        return [];
    }
  };

  const url = (file: FileToPreview): FilePreviewProps => {
    const filename =
      signature.title + (signature.title.toLowerCase().endsWith(".pdf") ? "" : ".pdf");
    if (context === "sealed" || (context === "signed" && isSealed)) {
      return {
        filename,
        file: { url: `/api/document/view-signed-document?ref=${file.reference}` },
      };
    } else {
      return {
        filename,
        file: { url: `/api/sign/download-document-for-signer/${file.reference}` },
      };
    }
  };

  switch (signature.documentType) {
    case "subscription_form": {
      return openSubscriptionForm(state, context, signature);
    }
    default:
      return [
        updateSignaturesStore(state, {
          documentPreview: new BatchPreviewModel(signature.title, url, files()),
        }),
      ];
  }
};

export const openSubscriptionForm = (
  state: PortfolioSignaturesReadyState,
  context: PortfolioSignaturesTab,
  signature: SignatureItem,
): DCEvent[] => {
  const batchPreviewModel = new BatchPreviewModel(signature.title, (file: FileToPreview) => {
    const filename = signature.title.toLowerCase().endsWith(".pdf")
      ? signature.title
      : `${signature.title} - ${file.name}.pdf`;
    return {
      filename,
      file: {
        url: `/api/document/view-subscription-form/${signature.documentId}/${signature.company.organisationNumber}?ref=${file.reference}`,
      },
    };
  });
  switch (context) {
    case "signed": {
      const reference = resolveUrnReference(signature.reference);
      batchPreviewModel.setFiles(
        reference !== undefined && reference.type === "subscriberId"
          ? [{ reference: reference.id }]
          : [],
      );
      return [updateSignaturesStore(state, { documentPreview: batchPreviewModel })];
    }
    case "sent":
    case "sealed": {
      return [
        updateSignaturesStore(state, {
          documentPreview: batchPreviewModel,
        }),
        signaturesApi.getSubscriptionStatus({
          signatureItem: signature,
          onSuccess: (res) => {
            batchPreviewModel.setFiles(mapFilesToPreviewFromSubscriptionStatus(ok(res.data)));
            return [];
          },
          onFailure: () => {
            batchPreviewModel.setError();
            return [];
          },
        }),
      ];
    }
    default:
      return [];
  }
};

export const fetchSigningStatus = (
  state: PortfolioSignaturesReadyState,
  dispatch: PortfolioPageDispatcher,
  signatureItem: SignatureItem,
): DCEvent[] => {
  const signingStatusDialogModel: ViewDocumentSigningStatusDialogModel =
    new ViewDocumentSigningStatusDialogModel({
      documentId: signatureItem.documentId,
      documentType: signatureItem.documentType,
      title: { translated: signatureItem.title },
      closeDialog: () => dispatch(closeSigningStatusDialog(state)),
      getSigningStatus: () => dispatch(fetchSigningStatus(state, dispatch, signatureItem)),
      resendDocumentForSigning: (signedDocumentId, data) =>
        dispatch(resendDocumentForSigning(signingStatusDialogModel, signedDocumentId, data)),
      confirmDeleteSignatureRequest: () =>
        dispatch(
          confirmCancelRequest({ signatureItem, signingStatusDialogModel, state, dispatch }),
        ),
      // download functionality can be enabled the same way as resendDocumentForSigning
      //downloadSignedDocument: () => dispatch({ type: "download-signed-document", entryId: entry.id }),
    });
  return [
    updateSignaturesStore(state, { signingStatusDialogModel }),
    signaturesApi.getSigningStatus({
      signatureItem,
      onSuccess: (res) => {
        signingStatusDialogModel.setSigningStatusResult(ok(res.data));
        return [];
      },
      onFailure: () => {
        signingStatusDialogModel.setSigningStatusResult(
          failure({ key: "dbot.getSigningStatusFailure" }),
        );
        return [];
      },
    }),
  ];
};

export const resendDocumentForSigning = (
  signingStatusDialogModel: ViewDocumentSigningStatusDialogModel,
  signedDocumentId: string,
  data: ResendDocumentForSigningData,
): DCEvent[] => {
  return [
    signaturesApi.resendDocumentForSigning({
      signedDocumentId,
      data,
      onSuccess: (res) => {
        signingStatusDialogModel.setSigningStatusResult(ok(res.data));
        return [];
      },
      onFailure: () => {
        signingStatusDialogModel.setSigningStatusResult(
          failure({ key: "dbot.summary.resendFailure" }),
        );
        return [];
      },
    }),
  ];
};

export const closeSigningStatusDialog = (state: PortfolioSignaturesReadyState): DCEvent[] => [
  {
    type: "update-store",
    data: {
      portfolioSignatures: { ...state, signingStatusDialogModel: undefined },
    },
  },
];

export const confirmCancelRequest = ({
  signatureItem,
  signingStatusDialogModel,
  state,
  dispatch,
}: {
  signatureItem: SignatureItem;
  signingStatusDialogModel: ViewDocumentSigningStatusDialogModel;
  state: PortfolioSignaturesReadyState;
  dispatch: EventDispatcher;
}): DCEvent[] => {
  const filterSignatures = (signatures: SignatureItem[]) =>
    signatures.filter((f) => f.documentId !== signatureItem.documentId);
  const signatures: SignaturesContext = {
    pending: filterSignatures(state.signaturesResponse.signatures.pending),
    sent: filterSignatures(state.signaturesResponse.signatures.sent),
    sealed: filterSignatures(state.signaturesResponse.signatures.sealed),
    signed: filterSignatures(state.signaturesResponse.signatures.signed),
  };

  const activeTab: PortfolioSignaturesTab =
    state.signaturesTab ?? findActiveSignaturesTab(state.signaturesTab, signatures);
  return [
    cancelSignatureOrReopenDocument({
      signature: signatureItem,
      onSuccess: () => {
        signingStatusDialogModel.onClose();
        return [
          showAlert({ key: "signature.cancel-signing-request-success-message" }, "success"),
          updateSignaturesStore(state, {
            ...state,
            signaturesResponse: {
              ...state.signaturesResponse,
              signatures,
            },
            companyFilter: filterCompaniesBySignature(activeTab, signatures),
            signaturesTabs: createSignaturesTabs(
              state.portfolio,
              state.signaturesResponse.hasAdminRole,
              signatures,
              activeTab,
              dispatch,
              state.contextCompany,
            ),
          }),
        ];
      },
      onFailure: () => [showGeneralError()],
    }),
  ];
};

export function cancelSignatureOrReopenDocument({
  signature,
  onSuccess,
  onFailure,
}: {
  signature: SignatureItem;
  onSuccess: () => DCEvent[];
  onFailure: () => DCEvent[];
}): DCEvent {
  return signature.signingRequestSource.type === "dbot"
    ? signaturesApi.reopenLockedDocument({
        documentId: signature.documentId,
        orgNo: signature.company.organisationNumber,
        onSuccess,
        onFailure,
      })
    : signaturesApi.cancelSignatureRequest({
        documentId: signature.documentId,
        onSuccess,
        onFailure,
      });
}

export function createSentForSigningLinkAction(
  signature: SignatureItem,
  dispatch: PortfolioPageDispatcher,
): SentForSigningLinkAction {
  const signingRequestSource = signature.signingRequestSource;
  const orgNo = signature.company.organisationNumber;
  switch (signingRequestSource.type) {
    case "dbot":
      return {
        icon: "sparkle",
        label: { key: "archive.projects" },
        title: { translated: signature.title },
        onClick: () =>
          dispatch([
            {
              type: "navigate",
              url: adminProjects(orgNo) + "/" + signingRequestSource.projectId,
            },
          ]),
      };
    case "archive": {
      let title: TMessage = { translated: signingRequestSource.parentTitle };
      let label: TMessage | undefined = { key: "header.archive" };
      if (signingRequestSource.documentType?.valueOf() === ArchiveDocumentType.FILE) {
        title = getSentForSigningTitle(signingRequestSource.parentTitle);
        label = undefined;
      } else if (signature.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE) {
        if (signingRequestSource.documentType === DocumentType.general_meeting) {
          title = { key: "meetings.general-meetings" };
          label = undefined;
        } else if (signingRequestSource.documentType === DocumentType.board_meeting) {
          title = { key: "meetings.board-meetings" };
          label = undefined;
        }
      }
      return {
        icon: "folder",
        label,
        title,
        onClick: () =>
          dispatch([
            navigate(
              documentLocation({
                orgNo,
                parentTitle: signingRequestSource.parentTitle,
                archiveRootFolder: signingRequestSource.archiveRootFolder?.folderType,
                folderType: signingRequestSource.folderType,
                folderId: signingRequestSource.parentId,
              }),
            ),
          ]),
      };
    }
  }
}

export const getCompanyItems = (
  tab: PortfolioSignaturesTab,
  signatures: SignaturesContext,
): CompanyItem[] => {
  switch (tab) {
    case "pending":
      return signatures.pending.map((s) => s.company);
    case "signed":
      return signatures.signed.map((s) => s.company);
    case "sent":
      return signatures.sent.map((s) => s.company);
    case "sealed":
      return signatures.sealed.map((s) => s.company);
  }
};

export const initPortfolioSignaturesAction = ({
  dispatch,
  portfolio,
  signaturesTab,
  store,
  contextCompany,
  isEmailVerified,
}: {
  dispatch: EventDispatcher;
  portfolio: SelectedPortfolioOption;
  signaturesTab?: PortfolioSignaturesTab;
  store: PortfolioPageStore;
  contextCompany?: string;
  header?: PortfolioHeader;
  isEmailVerified?: boolean;
}): DCEvent[] => {
  const portfolioSignatures = store.portfolioSignatures;

  // keep state when switch tabs
  if (
    signaturesTab &&
    portfolioSignatures &&
    isSamePortfolio(portfolio, portfolioSignatures.portfolio)
  ) {
    if (
      portfolioSignatures.signaturesTab !== signaturesTab &&
      portfolioSignatures.type === "portfolio-signatures-ready"
    ) {
      const ensuredSignaturesTab = findActiveSignaturesTab(
        signaturesTab,
        portfolioSignatures.signaturesResponse.signatures,
      );
      return [
        updateSignaturesStore(portfolioSignatures, {
          signaturesTab: ensuredSignaturesTab,
          signaturesTabs: createSignaturesTabs(
            portfolio,
            portfolioSignatures.signaturesResponse.hasAdminRole,
            portfolioSignatures.signaturesResponse.signatures,
            ensuredSignaturesTab,
            dispatch,
            contextCompany,
          ),
          companyFilter: filterCompaniesBySignature(
            ensuredSignaturesTab,
            portfolioSignatures.signaturesResponse.signatures,
          ),
        }),
      ];
    }
    return portfolioSignatures.type === "portfolio-signatures-ready"
      ? [updateSignaturesStore(portfolioSignatures, { isEmailVerified })]
      : [];
  }

  return [
    {
      type: "update-store",
      data: {
        portfolioSignatures: {
          type: "loading-portfolio-signatures",
          portfolio,
          signaturesTab,
        },
      },
    },
    signaturesApi.getSignatures({
      portfolio,
      contextCompany,
      onSuccess: (res) => {
        const activeSignaturesTab =
          portfolioSignatures?.signaturesTab ??
          findActiveSignaturesTab(signaturesTab, res.data.signatures);

        return [
          { type: "update-store", data: { followUpCounts: toFollowUpCounts(res.data) } },
          {
            type: "update-store",
            data: {
              portfolioSignatures: {
                type: "portfolio-signatures-ready",
                signaturesResponse: res.data,
                portfolio,
                signaturesTab: activeSignaturesTab,
                signatureSearchModel: new StringFieldModel(),
                signaturesTabs: createSignaturesTabs(
                  portfolio,
                  res.data.hasAdminRole,
                  res.data.signatures,
                  activeSignaturesTab,
                  dispatch,
                  contextCompany,
                ),
                displayGlobalSignatureEmptyState: shouldDisplayGlobalSignatureEmptyState(
                  res.data.signatures,
                ),
                portfolioSettingsModel: {
                  name: new StringFieldModel({ value: res.data.portfolio?.name, required: true }),
                },
                companyFilter: filterCompaniesBySignature(activeSignaturesTab, res.data.signatures),
                contextCompany,
                documentSignaturesMessage:
                  contextCompany !== undefined
                    ? getDocumentSignaturesMessage(res.data.otherPendingDocumentsCount)
                    : undefined,
                portfolioCreationInfo: portfolioCreationInfo(res.data.portfolio),
                isEmailVerified,
              },
            },
          },
        ];
      },
      onFailure: () => [
        {
          type: "update-store",
          data: { portfolioSignatures: { type: "invalid-portfolioid", portfolio } },
        },
      ],
    }),
  ];
};

export function groupBySigningMethod(
  pending: SignatureItem[],
): { signingMethod: SigningMethod; count: number }[] {
  const grouped = pending.reduce(
    (acc, item) => {
      acc[item.signingMethod] = (acc[item.signingMethod] || 0) + 1;
      return acc;
    },
    {} as { [key in SigningMethod]: number },
  );

  return Object.entries(grouped).map(([signingMethod, count]) => ({
    signingMethod: signingMethod as SigningMethod,
    count,
  }));
}

export const getDocumentSignaturesMessage = (numberOfDocuments: number): TMessage | undefined => {
  if (numberOfDocuments === 0) {
    return undefined;
  } else if (numberOfDocuments === 1) {
    return {
      key: "signatures.notes.one.document-to-sign",
      params: {
        url: `/portfolio/all/follow-up/signatures`,
      },
    };
  }
  return {
    key: "signatures.notes.other.documents-to-sign",
    params: { count: numberOfDocuments, url: "/portfolio/all/follow-up/signatures" },
  };
};

export const updateSignaturesStore = <T extends PortfolioSignaturesState>(
  state: T,
  update: Partial<T>,
): DCEvent => ({
  type: "update-store",
  data: {
    portfolioSignatures: {
      ...state,
      ...update,
    },
  },
});

export function shouldDisplayGlobalSignatureEmptyState(signature?: SignaturesContext): boolean {
  return (
    signature === undefined ||
    (signature.pending.length === 0 &&
      signature.signed.length === 0 &&
      signature.sent.length === 0 &&
      signature.sealed.length === 0)
  );
}

export const findActiveSignaturesTab = (
  signaturesTab?: PortfolioSignaturesTab,
  signatures?: SignaturesContext,
): PortfolioSignaturesTab => {
  if (signaturesTab !== undefined) {
    return signaturesTab;
  }
  if (signatures !== undefined) {
    if (signatures.pending.length > 0) {
      return "pending";
    } else if (signatures.signed.length > 0) {
      return "signed";
    } else if (signatures.sent.length > 0) {
      return "sent";
    } else if (signatures.sealed.length > 0) {
      return "sealed";
    }
  }
  return "pending";
};

export function createSignaturesTabs(
  portfolio: SelectedPortfolioOption,
  hasAdminRole: boolean,
  signatures: SignaturesContext | undefined,
  active: PortfolioSignaturesTab | undefined,
  dispatch: (events: DCEvent[]) => void,
  contextCompany?: string,
): TabButtonProps[] {
  const includeAdminTabs =
    hasAdminRole && !(portfolio.type === "with-board-role" || portfolio.type === "my-investments");
  const isInContextCompany = contextCompany !== undefined;
  return signatures
    ? compact([
        pendingTab(signatures, contextCompany),
        signedTab(signatures, contextCompany),
        includeAdminTabs && sentTab(signatures, contextCompany),
        includeAdminTabs && sealedTab(signatures, contextCompany),
      ]).map((i) => ({
        ...i,
        isActive: i.id === active,
        onSelect: () =>
          dispatch([
            isInContextCompany
              ? navigateToSignaturesTabInContextCompany(
                  i.id as PortfolioSignaturesTab,
                  contextCompany,
                )
              : navigateToSignaturesTabAction(portfolio, i.id as PortfolioSignaturesTab),
          ]),
      }))
    : [];
}

const pendingTab = (signatures: SignaturesContext, contextCompany?: string): TabButtonProps => ({
  id: "pending",
  label: {
    key: "signatures.pending",
    params: {
      count:
        contextCompany === undefined
          ? signatures.pending.length
          : signatures.pending.filter((f) => f.company.organisationNumber === contextCompany)
              .length,
    },
  },
  onSelect: noop,
});

const signedTab = (signatures: SignaturesContext, contextCompany?: string): TabButtonProps => ({
  id: "signed",
  label: {
    key: "signatures.signed-by-you",
    params: {
      count:
        contextCompany === undefined
          ? signatures.signed.length
          : signatures.signed.filter((f) => f.company.organisationNumber === contextCompany).length,
    },
  },
  onSelect: noop,
});

const sentTab = (signatures: SignaturesContext, contextCompany?: string): TabButtonProps => ({
  id: "sent",
  label: {
    key: "signatures.sent-for-signing",
    params: {
      count:
        contextCompany === undefined
          ? signatures.sent.length
          : signatures.sent.filter((f) => f.company.organisationNumber === contextCompany).length,
    },
  },
  onSelect: noop,
});

const sealedTab = (signatures: SignaturesContext, contextCompany?: string): TabButtonProps => ({
  id: "sealed",
  label: {
    key: "signatures.sealed",
    params: {
      count:
        contextCompany === undefined
          ? signatures.sealed.length
          : signatures.sealed.filter((f) => f.company.organisationNumber === contextCompany).length,
    },
  },
  onSelect: noop,
});

const fileUploadConfig: FileUploadConfig = {
  supportedFileTypes: [{ extensions: ["pdf"], magicBytes: [] }],
  maxFileSize: 5120 * 5120,
};

export const prepareSignaturesState = ({
  store,
  dispatch,
  portfolio,
  onRemovePortfolio,
  onUpdatePortfolio,
  aiClient,
  user,
}: {
  store: PortfolioPageStore;
  dispatch: PortfolioPageDispatcher;
  portfolio: SelectedPortfolioOption;
  onRemovePortfolio: (portfolioId: string) => void;
  onUpdatePortfolio: (portfolio: Portfolio) => void;
  aiClient: AIClient;
  user: User | null;
  userPrefs: UserPrefs;
  header?: PortfolioHeader;
}): PreparedPortfolioSignaturesState => {
  const state = store.portfolioSignatures;
  switch (state?.type) {
    case undefined:
    case "loading-portfolio-signatures":
      return {
        state: state ?? { type: "loading-portfolio-signatures", portfolio },
      };
    case "invalid-portfolioid":
      return { state };
    case "portfolio-signatures-ready":
      return {
        state,
        actions: {
          navigateToArchiveFolder: (orgNo: string, folderId: string) =>
            dispatch([{ type: "navigate", url: adminArchive({ orgNo, folderId }) }]),
          navigateToProject: (orgNo: string, projectId: string) =>
            dispatch([{ type: "navigate", url: adminProjects(orgNo) + projectId }]),
          mapToSentForSigningLinkAction: (signature) =>
            createSentForSigningLinkAction(signature, dispatch),
          getSigningStatus: (signature: SignatureItem) =>
            dispatch(fetchSigningStatus(state, dispatch, signature)),
          openDocument: (signature: SignatureItem, context: PortfolioSignaturesTab) =>
            dispatch(openDocument(state, context, signature)),
          closeDocumentPreview: () => dispatch(closeDocumentPreview(state)),
          filterSignatures: (locale) => filterSignaturesWithText(state, locale),
          openBatchSigningPage: (signingMethod: SigningMethod) =>
            dispatch(openBatchSigningPage(signingMethod)),
          openPortfolioSettings: () => dispatch(togglePortfolioSettings(state, true)),
          closePortfolioSettings: () => dispatch(togglePortfolioSettings(state, false)),
          savePortfolioSettings: () => dispatch(savePortfolioSettings(state, onUpdatePortfolio)),
          confirmDissolvePortfolio: () => dispatch(toggleConfirmDissolvePortfolio(state, true)),
          closeConfirmDissolvePortfolio: () =>
            dispatch(toggleConfirmDissolvePortfolio(state, false)),
          dissolvePortfolio: () => dispatch(dissolvePortfolio(state, onRemovePortfolio)),
          openSendForSigningDialog: () =>
            dispatch(openSelectDocumentsForSigningDialog(state, fileUploadConfig)),
          selectDocumentsForSigning: (file: File[]) =>
            dispatch(validateDocumentsForSigning(state, file)),
          openSendDocumentsForSigningDialog: () => {
            dispatch(openSendDocumentsForSigningDialog({ state, aiClient, user }));
          },
          removeDocumentForSigning: (file) => dispatch(removeDocumentForSigning(state, file)),
          setCompanyItemDestination: (file, item) =>
            dispatch(setCompanyDestination(state, file, item)),
          onSelectDestinationFolder: (sendForSigningItem, folder) =>
            dispatch(selectDestinationFolder(state, sendForSigningItem, folder)),
          closeForSigningDialog: () => dispatch(closeSignDocumentDialog(state)),
          submitDocumentsForSigning: () => dispatch(submitDocumentsForSigning(state, dispatch)),
          setSelectedItemIndex: (index) => {
            dispatch(setSelectedDocumentForSigningIndex(state, index));
          },
          nextItem: () => dispatch(selectNextDocument(state)),
          previousItem: () => dispatch(selectPreviousDocument(state)),
          setSelectedCompanyItemIndex: (index) => {
            dispatch(setSelectedCompanyForSigningIndex(state, index));
          },
          nextCompanyItem: () => dispatch(selectNextCompany(state)),
          previousCompanyItem: () => dispatch(selectPreviousCompany(state)),
          closeDuplicateAlertInfo: () => dispatch(closeDuplicateAlertInfo(state)),
          verifyEmail: () => dispatch([showVerifyEmailDialog]),
        },
      };
  }
};

export const setCompanyDestination = (
  state: PortfolioSignaturesReadyState,
  file: File,
  item: CompanyItem,
): DCEvent[] => {
  const sendForSigningState: SendForSigningState = {
    ...state.sendForSigningState!,
    sendDocumentForSigningItems: state.sendForSigningState!.sendDocumentForSigningItems.map((x) =>
      x.file === file
        ? { ...x, companyItem: item, destination: undefined, destinationFolderCandidates: [] }
        : x,
    ),
  };
  return [
    updateSignaturesStore(state, { sendForSigningState }),
    signaturesApi.getDestinationFolders({
      orgNo: item.organisationNumber,
      onSuccess: (res) => [
        updateSignaturesStore(state, {
          sendForSigningState: {
            ...sendForSigningState,
            sendDocumentForSigningItems: sendForSigningState.sendDocumentForSigningItems.map((x) =>
              x.file === file ? { ...x, destinationFolderCandidates: res.data.entries } : x,
            ),
          },
        }),
      ],
      onFailure: () => [showGeneralError()],
    }),
  ];
};

export const selectDestinationFolder = (
  state: PortfolioSignaturesReadyState,
  sendDocumentForSigningItem: SendDocumentForSigningItem,
  folder: DestinationFolder,
): DCEvent[] => {
  return [
    updateSignaturesStore(state, {
      sendForSigningState: {
        ...state.sendForSigningState!,
        sendDocumentForSigningItems: state.sendForSigningState!.sendDocumentForSigningItems.map(
          (s) =>
            sendDocumentForSigningItem.file === s.file
              ? {
                  ...sendDocumentForSigningItem,
                  destination: folder,
                }
              : s,
        ),
      },
    }),
  ];
};

export const setSelectedDocumentForSigningIndex = (
  state: PortfolioSignaturesReadyState,
  index: number,
): DCEvent[] => {
  return [
    updateSignaturesStore(state, {
      sendForSigningState: {
        ...state.sendForSigningState!,
        selectedItemIndex: index,
      },
    }),
  ];
};

export const selectNextDocument = (state: PortfolioSignaturesReadyState): DCEvent[] => {
  const currentIndex = state.sendForSigningState!.selectedItemIndex ?? 0;
  const index = (currentIndex + 1) % getSigningItemsForSigning(state.sendForSigningState!).length;
  return [
    updateSignaturesStore(state, {
      sendForSigningState: {
        ...state.sendForSigningState!,
        selectedItemIndex: index,
      },
    }),
  ];
};

export const selectPreviousDocument = (state: PortfolioSignaturesReadyState): DCEvent[] => {
  const currentIndex = state.sendForSigningState!.selectedItemIndex ?? 0;
  const signingItemsForSigning = getSigningItemsForSigning(state.sendForSigningState!);
  const index = currentIndex - 1;
  return [
    updateSignaturesStore(state, {
      sendForSigningState: {
        ...state.sendForSigningState!,
        selectedItemIndex: index >= 0 ? index : signingItemsForSigning.length - 1,
      },
    }),
  ];
};

export const setSelectedCompanyForSigningIndex = (
  state: PortfolioSignaturesReadyState,
  index: number,
): DCEvent[] => {
  return [
    updateSignaturesStore(state, {
      sendForSigningState: {
        ...state.sendForSigningState!,
        selectedItemIndex: undefined,
        selectedCompanyItemIndex: index,
      },
    }),
  ];
};

export const selectNextCompany = (state: PortfolioSignaturesReadyState): DCEvent[] => {
  const currentIndex = state.sendForSigningState!.selectedCompanyItemIndex ?? 0;
  const index = (currentIndex + 1) % getCompanyItemsForSigning(state.sendForSigningState!).length;
  return [
    updateSignaturesStore(state, {
      sendForSigningState: {
        ...state.sendForSigningState!,
        selectedItemIndex: 0,
        selectedCompanyItemIndex: index,
      },
    }),
  ];
};

export const selectPreviousCompany = (state: PortfolioSignaturesReadyState): DCEvent[] => {
  const currentIndex = state.sendForSigningState!.selectedCompanyItemIndex ?? 0;
  const signingItemsForSigning = getSigningItemsForSigning(state.sendForSigningState!);
  const index = currentIndex - 1;
  return [
    updateSignaturesStore(state, {
      sendForSigningState: {
        ...state.sendForSigningState!,
        selectedItemIndex: 0,
        selectedCompanyItemIndex: index >= 0 ? index : signingItemsForSigning.length - 1,
      },
    }),
  ];
};

export const removeDocumentForSigning = (
  state: PortfolioSignaturesReadyState,
  file: File,
): DCEvent[] => {
  const sendForSigningState: SendForSigningState = {
    ...state.sendForSigningState!,
    sendDocumentForSigningItems: state.sendForSigningState!.sendDocumentForSigningItems.filter(
      (f) => f.file !== file,
    ),
  };
  return [updateSignaturesStore(state, { sendForSigningState })];
};

export const canSendForSigning = (state: SendForSigningState) =>
  state.sendDocumentForSigningItems.length > 0 &&
  state.sendDocumentForSigningItems.every(
    (f) => f.companyItem !== undefined && f.destination !== undefined,
  );

export const getFileDestinations = (state: SendForSigningState): FileDestination[] => {
  const selectedDestination = state.signersForm?.isBatchSigning
    ? undefined
    : state.signersForm?.selectedDestinationFolder;

  return state.sendDocumentForSigningItems.map((x) => ({
    folderId: selectedDestination?.id ?? x.destination!.id,
    fileName: x.file.name,
    orgNo: x.companyItem!.organisationNumber,
  }));
};

export const sendForSigningDialogTitle = (state?: SendDocumentForSigningItem[]) => {
  return state && state.length === 1 ? state[0].file.name : state?.length;
};

const submitDocumentsForSigning = (
  state: PortfolioSignaturesReadyState,
  dispatch: PortfolioPageDispatcher,
): DCEvent[] => {
  const activeTab: PortfolioSignaturesTab =
    state.signaturesTab ??
    findActiveSignaturesTab(state.signaturesTab, state.signaturesResponse.signatures);
  const onSuccess = () => {
    return [
      showAlert({ key: "submit-for-signing-success.message" }, "success"),
      updateSignaturesStore(state, {
        sendForSigningState: undefined,
      }),
      signaturesApi.getSignatures({
        portfolio: state.portfolio,
        onSuccess: (res) => [
          updateSignaturesStore(state, {
            sendForSigningState: undefined,
            signaturesResponse: res.data,
            signaturesTabs: createSignaturesTabs(
              state.portfolio,
              res.data.hasAdminRole,
              res.data.signatures,
              state.signaturesTab,
              dispatch,
              state.contextCompany,
            ),
            displayGlobalSignatureEmptyState: shouldDisplayGlobalSignatureEmptyState(
              res.data.signatures,
            ),
            companyFilter: filterCompaniesBySignature(activeTab, res.data.signatures),
          }),
        ],
        onFailure: () => [showGeneralError()],
        contextCompany: state.contextCompany,
      }),
    ];
  };
  const sendForSigningState = state.sendForSigningState!;
  const form = sendForSigningState.signersForm!;
  form.validate();
  if (!form.isValid) {
    return [];
  }
  const files: File[] = sendForSigningState.sendDocumentForSigningItems.map((f) => f.file) ?? [];
  const formData = toFormData(files);
  formData.append("emailBody", form.emailBody.textModels[0].model.inputValue ?? "");
  formData.append("destinations", JSON.stringify(getFileDestinations(sendForSigningState)));
  formData.append("signers", JSON.stringify(form.items.map((s) => s.toSigner()).map(mapToSigners)));
  formData.append("signingSettings", JSON.stringify(form.toSigningSettings()));
  return [
    updateSignaturesStore(state, {
      sendForSigningState: {
        ...sendForSigningState,
        isSubmitting: true,
      },
    }),
    signaturesApi.submitDocumentsForSigning({
      formData,
      onSuccess,
      onFailure: () => [
        showGeneralError(),
        updateSignaturesStore(state, {
          sendForSigningState: {
            ...sendForSigningState,
            isSubmitting: false,
          },
        }),
      ],
    }),
  ];
};

export const getTitle = (items?: SendDocumentForSigningItem[]): TMessage => {
  return items?.length === 1
    ? {
        key: "send-x-for-signing",
        params: {
          x: sendForSigningDialogTitle(items),
        },
      }
    : {
        key: "signatures.send-documents-for-signing-title",
        params: {
          n: sendForSigningDialogTitle(items),
        },
      };
};

export const getEmailBody = (user: User | null, item?: SendDocumentForSigningItem[]): TMessage => {
  return item?.length === 1
    ? {
        key: "signatures.send-documents-for-signing.email-body.singular",
        params: {
          senderName: getFullName(user),
          fileName: sendForSigningDialogTitle(item),
        },
      }
    : {
        key: "signatures.send-documents-for-signing.email-body.plural",
        params: {
          senderName: getFullName(user),
          n: sendForSigningDialogTitle(item),
        },
      };
};

export const getDocumentPreview = (state: SendForSigningState): FilePreviewProps | undefined => {
  return when(getSelectedSigningItem(state), (item) => ({
    filename: item.file.name,
    file: { data: item.file },
    width: "sm",
  }));
};

export const prepareDocumentsForSigning = (
  state: SendForSigningState,
  actions: SignatureActions,
): DocumentsForPreview | undefined => {
  if (state.sendDocumentForSigningItems.length > 0) {
    const signingItems = getSigningItemsForSigning(state);
    return {
      documents: signingItems.map((item) => ({
        id: item.file.name,
        name: item.file.name,
      })),
      selectedDocument: when(getSelectedSigningItem(state), (item) => ({
        id: item.file.name,
        name: item.file.name,
      })),
      selectDocument: ({ name }) => {
        const index = state.sendDocumentForSigningItems.findIndex((x) => x.file.name === name);
        actions.setSelectedItemIndex(index ?? 0);
      },
      previousDocument: actions.previousItem,
      nextDocument: actions.nextItem,
    };
  }
};

export const prepareCompaniesForSigning = (
  state: SendForSigningState,
  actions: SignatureActions,
): CompaniesForPreview | undefined => {
  if (state.sendDocumentForSigningItems.length > 0) {
    const companies = [
      ...new Set(state.sendDocumentForSigningItems.map((item) => item.companyItem)),
    ].filter((d) => d !== undefined);
    return {
      companies: getCompanyItemsForSigning(state),
      selectedCompany: when(getSelectedCompanyItem(state), (item) => item),
      selectCompany: (company) => {
        const index = companies.findIndex(
          (x) => x?.organisationNumber === company.organisationNumber,
        );
        actions.setSelectedCompanyItemIndex(index ?? 0);
      },
      previousCompany: actions.previousCompanyItem,
      nextCompany: actions.nextCompanyItem,
    };
  }
};

export const closeDuplicateAlertInfo = (state: PortfolioSignaturesReadyState): DCEvent[] => {
  return [
    updateSignaturesStore(state, {
      ...state,
      duplicateFileUploading: undefined,
    }),
  ];
};
